import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';

import { FeatureFlag } from 'constants/featureFlags';
import { checkIsPurchaseFlow, checkIsRefinanceFlow } from 'utils/loanApplication';

import {
  selectCommonInfoLoanApplication,
  selectFinancialInfoOwnersOptions,
} from 'store/loanApplications/selectors';

import { SectionContentPropTypes } from '../SectionContent.constants';
import { customOnAutocompleteChange } from '../SectionContent.utils';

import { getConfig, getConfigFlags } from './Assets.config';
import { VALIDATION_DELAY_MS } from './Assets.constants';
import { getCustomAssetsSchema, normalizeForm } from './Assets.utils';

const Assets = ({
  form,
  handleChange,
  isLoading,
  setInForm,
  setForm,
  validateField,
  validationErrors,
  isSaving,
  recordId,
  setRecordConfig,
  initialFormValues,
  data,
  isDeleted,
  schema,
  setSchema,
  isNewRecord,
  borrowerId,
  pendingChanges,
  disabledPendingChangesHighlight,
  isOwnersClicked,
}) => {
  const ownersOptions = useSelector(selectFinancialInfoOwnersOptions);

  const { loanPurpose } = useSelector(selectCommonInfoLoanApplication);

  const isRefinanceFlow = checkIsRefinanceFlow(loanPurpose);
  const isPurchaseFlow = checkIsPurchaseFlow(loanPurpose);

  const isAssetTypeSourceOfGrantEnabled = useFeatureToggle(FeatureFlag.EnableSourceOfGrant);

  const recordForm = form[recordId];
  const type = recordForm?.type?.value;
  const isDeposited = recordForm?.isDeposited;
  const sourceType = recordForm?.sourceType?.value;
  const cashOrMarketValue = recordForm?.cashOrMarketValue;
  const { isGrantType, isAssetTypeGift } = getConfigFlags({ type });
  const isCustomValidation = isGrantType || isAssetTypeGift;
  const isVerified = data[recordId]?.isVerified;

  const cashOrMarketValueRef = useRef(null);

  const onChangeAssetType = useCallback(
    (e, value) => {
      const customAction = () => {
        if (cashOrMarketValueRef.current === undefined) {
          return;
        }

        setTimeout(
          () => validateField(`[${recordId}].cashOrMarketValue`, cashOrMarketValueRef.current),
          VALIDATION_DELAY_MS,
        );
      };

      customOnAutocompleteChange({ e, value, validateField, setInForm, customAction });
    },
    [validateField, setInForm, recordId],
  );

  const config = useMemo(
    () =>
      getConfig({
        recordId,
        ownersOptions,
        type,
        isPurchaseFlow,
        isRefinanceFlow,
        isAssetTypeSourceOfGrantEnabled,
        isDeposited,
        sourceType,
        isVerified,
        onChangeAssetType,
        isNewRecord,
        isOwnersClicked,
      }),
    [
      recordId,
      ownersOptions,
      type,
      isPurchaseFlow,
      isRefinanceFlow,
      isAssetTypeSourceOfGrantEnabled,
      isDeposited,
      sourceType,
      isVerified,
      onChangeAssetType,
      isNewRecord,
      isOwnersClicked,
    ],
  );

  useEffect(() => {
    if (isDeleted) {
      return;
    }

    setRecordConfig(config, recordId);
  }, [config, setRecordConfig, isDeleted, recordId]);

  useEffect(() => {
    const customSchema = getCustomAssetsSchema({
      schema,
      isCustomValidation,
      isPurchaseFlow,
      recordId,
    });

    setTimeout(() => setSchema(customSchema));
  }, [setSchema, schema, isCustomValidation, isPurchaseFlow, recordId]);

  useEffect(() => {
    setForm(form =>
      normalizeForm({
        form,
        recordId,
        isPurchaseFlow,
        isRefinanceFlow,
        isAssetTypeSourceOfGrantEnabled,
        isNewRecord,
        borrowerId,
        ownersOptions,
      }),
    );
  }, [
    data,
    initialFormValues,
    recordId,
    isPurchaseFlow,
    isRefinanceFlow,
    isAssetTypeSourceOfGrantEnabled,
    setForm,
    isNewRecord,
    borrowerId,
    ownersOptions,
  ]);

  useEffect(() => {
    cashOrMarketValueRef.current = cashOrMarketValue;
  }, [cashOrMarketValue]);

  return (
    <FormGenerator
      isNestedForm
      config={config}
      disabledPendingChangesHighlight={disabledPendingChangesHighlight}
      form={form}
      formName={FormNames.ASSETS}
      handleChange={handleChange}
      initialFormValues={initialFormValues}
      isLoading={isSaving || isLoading}
      numberOfColumns={4}
      pendingChanges={pendingChanges}
      setInForm={setInForm}
      validateField={validateField}
      validationErrors={validationErrors}
    />
  );
};

Assets.propTypes = {
  ...SectionContentPropTypes,
};

export default Assets;
